import { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Check } from 'lucide-react';

/**
 * SocialProofToast — bottom-left floating notification that rotates
 * "Someone just joined our Telegram channel" style messages, inspired by
 * onlineearnacademy.com but restyled to match AlveeTrade's KINGDM theme.
 *
 * • Names pool: mix of BD + wider region so it feels authentic for the /join audience.
 * • Cities pool: real BD divisions + a few global.
 * • Each toast stays ~6s, then hides ~10-16s (random) before next one.
 * • Suppresses on very small screens by shifting to bottom-center safe zone.
 */

const NAMES = [
  'Rahim', 'Karim', 'Fahim', 'Sadia', 'Nusrat', 'Arif', 'Sabbir', 'Tanvir',
  'Rifat', 'Mahin', 'Sohan', 'Rakib', 'Ashik', 'Tania', 'Farhana', 'Riya',
  'Imran', 'Mahi', 'Sifat', 'Rezaul', 'Tahmid', 'Sujan', 'Reza', 'Nayeem',
  'Shakib', 'Rana', 'Rohan', 'Habib', 'Nasir', 'Anik', 'Sami', 'Zubayer',
  'Faisal', 'Yasin', 'Ibrahim', 'Tamim', 'Emon', 'Rana', 'Sagor', 'Piyash',
  'Mustafa', 'Zayed', 'Hamid', 'Kabir', 'Aminul', 'Sayem', 'Ratul', 'Jamil',
];

const CITIES = [
  'Dhaka', 'Chattogram', 'Sylhet', 'Rajshahi', 'Khulna', 'Barishal', 'Rangpur',
  'Mymensingh', 'Cumilla', 'Cox\u2019s Bazar', 'Narayanganj', 'Gazipur', 'Bogura',
  'Jashore', 'Kushtia', 'Faridpur', 'Tangail', 'Noakhali',
];

function pick(arr) { return arr[Math.floor(Math.random() * arr.length)]; }
// Fast rotation per user request: new popup every ~1 second.
const VISIBLE_MS = 1800;        // toast visible for 1.8s
const GAP_MS = 1000;            // 1 second gap between toasts

export default function SocialProofToast({ isBangla = true, logoUrl }) {
  const [current, setCurrent] = useState(null); // {id, name, city}
  const idRef = useRef(0);
  const showTimerRef = useRef(null);
  const hideTimerRef = useRef(null);

  const T = useMemo(
    () =>
      isBangla
        ? {
            verb: 'এইমাত্র যোগ দিলেন',
            channel: 'AlveeFx Telegram Channel',
            live: 'লাইভ',
            justNow: 'এইমাত্র',
          }
        : {
            verb: 'just joined',
            channel: 'AlveeFx Telegram Channel',
            live: 'LIVE',
            justNow: 'just now',
          },
    [isBangla]
  );

  useEffect(() => {
    function scheduleNext(delay) {
      showTimerRef.current = window.setTimeout(() => {
        idRef.current += 1;
        setCurrent({ id: idRef.current, name: pick(NAMES), city: pick(CITIES) });
        // hide after VISIBLE_MS
        hideTimerRef.current = window.setTimeout(() => {
          setCurrent(null);
          scheduleNext(GAP_MS);
        }, VISIBLE_MS);
      }, delay);
    }
    // First popup after a short delay (1s) so it appears after page settles.
    scheduleNext(1000);
    return () => {
      if (showTimerRef.current) clearTimeout(showTimerRef.current);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, []);

  function dismiss() {
    setCurrent(null);
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
  }

  return (
    <div
      className="fixed z-40 top-4 sm:top-5 left-1/2 -translate-x-1/2 w-[calc(100vw-24px)] max-w-[360px] pointer-events-none"
      data-testid="social-proof-container"
    >
      <AnimatePresence>
        {current && (
          <motion.div
            key={current.id}
            initial={{ opacity: 0, y: -22, scale: 0.94 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -12, scale: 0.96 }}
            transition={{ duration: 0.28, ease: [0.2, 0.7, 0.2, 1] }}
            className="pointer-events-auto"
            data-testid="social-proof-toast"
          >
            <button
              type="button"
              onClick={dismiss}
              aria-label="Dismiss"
              className="group flex items-center gap-3 px-3.5 py-2.5 rounded-xl bg-[rgba(10,10,14,0.86)] backdrop-blur-md border border-white/12 shadow-[0_18px_50px_-14px_rgba(0,0,0,0.7)] max-w-[340px] text-left"
            >
              {/* Avatar with live pulse dot */}
              <div className="relative shrink-0">
                <div
                  className="h-10 w-10 rounded-full bg-[rgba(34,158,217,0.14)] border border-[rgba(42,171,238,0.45)] flex items-center justify-center overflow-hidden ring-1 ring-white/5"
                >
                  {logoUrl ? (
                    <img
                      src={logoUrl}
                      alt="AlveeFx"
                      className="h-full w-full object-cover"
                      draggable={false}
                    />
                  ) : (
                    <Send className="h-4 w-4 text-[hsl(199_84%_56%)]" />
                  )}
                </div>
                <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-[hsl(140_70%_50%)] ring-2 ring-[rgba(10,10,14,0.9)]">
                  <span className="absolute inset-0 rounded-full bg-[hsl(140_70%_50%)] animate-ping opacity-60" />
                </span>
              </div>

              {/* Content */}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 text-[13px] leading-tight">
                  <span className="font-semibold text-white truncate">
                    {current.name}
                  </span>
                  <span className="text-[hsl(0_0%_72%)] truncate">
                    {T.verb}
                  </span>
                  <Check className="h-3.5 w-3.5 text-[hsl(199_84%_56%)] shrink-0" />
                </div>
                <div className="mt-0.5 flex items-center gap-1.5 text-[10.5px] text-muted-foreground">
                  <span className="inline-flex items-center gap-1 rounded-full bg-[rgba(46,204,113,0.14)] border border-[rgba(46,204,113,0.35)] px-1.5 py-[1px] text-[9px] font-semibold uppercase tracking-[0.12em] text-[hsl(140_60%_66%)]">
                    <span className="h-1.5 w-1.5 rounded-full bg-[hsl(140_70%_50%)]" />
                    {T.live}
                  </span>
                  <span className="truncate">
                    {T.channel} · {current.city}
                  </span>
                </div>
              </div>
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
